<template>
  <Modal
    class="scoped"
    :modal-style="{ maxWidth: '900px' }"
    :title="`Briefs Matching ${mention.type}#${mention.id}`"
  >
    <ul>
      <li v-if="mention.created_at">
        Created at:
        {{
          formatIntlDate(mention.created_at, {
            dateStyle: "short",
            timeStyle: "long",
          })
        }}
      </li>
      <li v-if="mention.published_at">
        Published at:
        {{
          formatIntlDate(mention.published_at, {
            dateStyle: "short",
            timeStyle: "long",
          })
        }}
      </li>
      <li v-if="sourceName">Source: {{ mention.source.name }}</li>
      <li v-if="mention.url">URL: {{ mention.url }}</li>
      <li v-if="mention.title">Title: {{ mention.title }}</li>
      <li
        v-if="mention.highlighted_text"
        class="scroll list"
      >
        Text: {{ mention.highlighted_text }}
      </li>
    </ul>
    <QCard class="q-mt-xl">
      <QForm
        class="full-width q-col-gutter-md"
        @submit="getRelatedBriefs"
      >
        <div class="card q-pa-md no-wrap full-height scroll">
          <h1>Check this Item against the Briefs of:</h1>
          <QSeparator />

          <OrganisationsDropdown
            v-model="selectedOrganisation"
            :emit-value="false"
            filled
          />
        </div>
        <div class="row q-py-md justify-center">
          <QBtn
            color="primary"
            label="Check"
            type="submit"
          />
        </div>
        <QTable
          v-if="matchResult"
          v-model:pagination="pagination"
          :columns="columns"
          :loading="loading"
          row-key="id"
          :rows="filteredRelatedBriefs"
          :rows-per-page-options="rowPerPage"
        >
          <template #top-row>
            <QTr>
              <QTd>
                <QInput
                  v-model="filter.id"
                  borderless
                  clearable
                  dense
                  placeholder="ID"
                  square
                />
              </QTd>
              <QTd>
                <QInput
                  v-model="filter.formattedType"
                  borderless
                  clearable
                  dense
                  placeholder="Type"
                  square
                />
              </QTd>
              <QTd>
                <QInput
                  v-model="filter.organisationName"
                  borderless
                  clearable
                  dense
                  placeholder="Organisation"
                  square
                />
              </QTd>
              <QTd>
                <QInput
                  v-model="filter.label"
                  borderless
                  clearable
                  dense
                  placeholder="Label"
                  square
                />
              </QTd>
              <QTd>
                <QInput
                  v-model="filter.userName"
                  borderless
                  clearable
                  dense
                  placeholder="User"
                  square
                />
              </QTd>
              <QTd>
                <QInput
                  v-model="filter.query"
                  borderless
                  clearable
                  dense
                  placeholder="Query"
                  square
                />
              </QTd>
              <QTd />
              <QTd />
              <QTd />
            </QTr>
          </template>

          <template #body="props">
            <QTr :props="props">
              <QTd
                key="id"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                <a
                  class="block"
                  :href="editStreamLink(props.row)"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {{ props.row.id }}
                </a>
              </QTd>
              <QTd
                key="formattedType"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                {{ props.row.formattedType }}
                <BriefTypeTags :brief="props.row" />
              </QTd>
              <QTd
                key="organisationName"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                <a
                  class="block"
                  :href="`organisations/${props.row.organisation.id}/edit/information`"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {{ props.row.organisationName }}
                </a>
              </QTd>
              <QTd
                key="label"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                {{ props.row.label }}
              </QTd>
              <QTd
                key="userName"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                <a
                  class="block"
                  :href="editUserLink(props.row)"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {{ props.row.userName }}
                </a>
              </QTd>
              <QTd
                key="query"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                <QExpansionItem label="Show full query">
                  <QCard>
                    <QCardSection class="full-text-section">
                      {{ props.row.query }}
                    </QCardSection>
                  </QCard>
                </QExpansionItem>
              </QTd>
              <QTd
                key="keywords"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                <ul class="q-pl-none box">
                  <li
                    v-for="keyword in props.row.keywords"
                    :key="keyword"
                    class="row selected-streams flex"
                  >
                    {{ keyword }}
                  </li>
                </ul>
              </QTd>
              <QTd
                key="match"
                class="wrap-table-content vertical-top"
                :props="props"
              >
                <QChip
                  class="q-mt-md"
                  :color="colorMatch(props.row.matchedBrief)"
                  size="md"
                  text-color="white"
                >
                  {{ matchesBrief(props.row.matchedBrief) }}
                </QChip>
              </QTd>
              <QTd
                key="related_briefs"
                class="wrap-table-content vertical-top q-gutter-xs"
                :props="props"
              >
                <QBtn
                  class="q-mr-lg"
                  color="primary"
                  dense
                  flat
                  icon="bug_report"
                  @click.prevent="openMatchDebuggerPage(props.row)"
                >
                  <StreemTooltip>Does it match?</StreemTooltip>
                </QBtn>
              </QTd>
            </QTr>
          </template>
        </QTable>
      </QForm>
    </QCard>
  </Modal>
</template>

<script>
import Modal from "shared/components/core/modals/Modal.vue";
import { filterMedia } from "shared/helpers/array";
import { formatIntlDate } from "shared/helpers/date";
import { dig } from "shared/helpers/object";

import BriefTypeTags from "src/components/briefs/BriefTypeTags.vue";
import OrganisationsDropdown from "src/components/common/OrganisationsDropdown.vue";

export default {
  name: "RelatedBriefsModal",
  components: {
    Modal,
    BriefTypeTags,
    OrganisationsDropdown,
  },
  props: {
    mention: {
      type: Object,
      default: () => ({}),
    },
    organisation: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      columns: [
        {
          name: "id",
          required: true,
          label: "ID",
          align: "left top",
          field: "id",
          sortable: true,
          style: "width: 10%",
          classes: "wrap-table-content",
        },
        {
          name: "formattedType",
          required: true,
          label: "Type",
          align: "left top",
          field: "formattedType",
          sortable: true,
          style: "width: 10%",
          classes: "wrap-table-content",
        },
        {
          name: "organisationName",
          required: true,
          label: "Organisation",
          align: "left top",
          field: "organisationName",
          sortable: true,
          style: "width: 10%",
          classes: "wrap-table-content",
        },
        {
          name: "label",
          required: true,
          label: "Label",
          align: "left top",
          field: "label",
          sortable: true,
          style: "width: 15%",
          classes: "wrap-table-content",
        },
        {
          name: "userName",
          required: true,
          label: "User",
          align: "left top",
          field: "userName",
          sortable: true,
          style: "width: 10%",
          classes: "wrap-table-content",
        },
        {
          name: "query",
          required: true,
          label: "Search Query",
          align: "left top",
          field: "query",
          sortable: true,
          style: "width: 25%",
          classes: "wrap-table-content",
        },
        {
          name: "keywords",
          required: true,
          label: "Keywords matched",
          align: "left top",
          field: "keywords",
          sortable: true,
          style: "width: 10%",
          classes: "wrap-table-content",
        },
        {
          name: "match",
          required: true,
          label: "Match?",
          align: "center top",
          field: "match",
          sortable: true,
          style: "width: 15%",
          classes: "wrap-table-content",
        },
        {
          name: "related_briefs",
          required: true,
          label: "Debug",
          align: "left top",
          style: "width: 15%",
          classes: "wrap-table-content",
        },
      ],
      filter: {
        id: "",
        label: "",
        organisationName: "",
        userName: "",
        formattedType: "",
      },
      selectedOrganisation: this.organisation,
      rowPerPage: [25, 50, 100],
      pagination: {
        rowsPerPage: 100,
        sortBy: "match",
        descending: true,
      },
      loading: false,
      matchResult: false,
      relatedBriefs: [],
    };
  },
  computed: {
    sourceName() {
      return this.mention.source?.name;
    },
    filteredRelatedBriefs() {
      return filterMedia(
        this.relatedBriefs.map((brief) => ({
          ...brief,
          organisationName: dig(brief, "organisation", "name"),
          userName: dig(brief, "user", "name"),
        })),
        this.filter
      );
    },
  },
  methods: {
    formatIntlDate,
    async getRelatedBriefs() {
      this.relatedBriefs = [];
      this.loading = true;
      this.matchResult = true;
      const selectedOrganisation = { ...this.selectedOrganisation };

      try {
        const promises = [this.loadMatchBriefs()];
        if (selectedOrganisation)
          promises.push(this.loadAllBriefs(selectedOrganisation.id));

        const [matchingBriefsResponse, orgBriefsResponse] =
          await Promise.all(promises);

        const matchingBriefsIds = new Set(
          matchingBriefsResponse.data.map((brief) => brief.id)
        );

        const matchingBriefs = matchingBriefsResponse.data.map((brief) => ({
          ...brief,
          formattedType: this.formattedType(brief.type),
          matchedBrief: true,
        }));

        this.relatedBriefs = matchingBriefs;

        if (orgBriefsResponse) {
          const nonMatchingBriefs = orgBriefsResponse.data
            .map((brief) => ({
              ...brief,
              type: "OrganisationBrief",
              query: brief.boolean_query,
              formattedType: this.formattedType("OrganisationBrief"),
              matchingBrief: false,
              organisation: {
                id: brief.organisation_id,
                name: selectedOrganisation.name,
              },
            }))
            .filter(
              (nonMatchBrief) => !matchingBriefsIds.has(nonMatchBrief.id)
            );

          this.relatedBriefs = matchingBriefs.concat(nonMatchingBriefs);
        }
      } catch (error) {
        this.$error("There was an error fetching related briefs.", error);
      } finally {
        this.loading = false;
      }
    },
    loadMatchBriefs() {
      return this.$streemApiAdmin.get("briefs/percolate", {
        params: {
          id: this.mention.id,
          type: this.mention.type,
          organisation_id: this.selectedOrganisation?.id,
        },
      });
    },
    loadAllBriefs(organisationId) {
      return this.$streemApiAdmin.get(`organisations/${organisationId}/briefs`);
    },
    formattedType(briefType) {
      if (briefType === "MentionStream") {
        return "Custom Mention Stream";
      }

      if (briefType === "OrganisationBrief") {
        return "Managed Brief";
      }

      if (briefType === "BookmarkStream") {
        return "Bookmark Stream";
      }

      if (briefType === "SocialStream") {
        return "Social Stream";
      }

      return "";
    },
    colorMatch(matchedBrief) {
      return matchedBrief ? "green" : "red";
    },
    matchesBrief(matchedBrief) {
      return matchedBrief ? "YES" : "NO";
    },
    editStreamLink(row) {
      switch (row.type) {
        case "OrganisationBrief":
          return `organisations/${row.organisation.id}/briefs/${row.id}/edit`;
        case "MentionStream":
        case "BookmarkStream":
          return `organisations/${row.organisation.id}/mention-streams/${row.id}/edit`;
        default:
          return "";
      }
    },
    editUserLink(row) {
      if (!row.userName) return "";

      return (
        `organisations/${row.organisation.id}/edit/users?` +
        `firstName=${row.user.first_name}&lastName=${row.user.last_name}`
      );
    },
    openMatchDebuggerPage(stream) {
      const itemId = this.mention.id;
      const itemType = this.mention.type;
      const organisationId = stream.organisation.id;
      const queryType = this.selectTypeForMatchDebugger(stream.formattedType);

      const queryId = this.selectTypeForMatchDebugger(stream.formattedType)
        ? stream.id
        : "";

      const path = `/match-debugger?itemId=${itemId}&itemType=${itemType}&organisationId=${organisationId}&queryType=${queryType}&queryId=${queryId}`;
      const route = this.$router.resolve(path);

      window.open(route.href);
    },
    selectTypeForMatchDebugger(formattedType) {
      let type = "";

      if (formattedType === "Managed Brief") {
        type = "briefs";
      } else if (formattedType === "Custom Mention Stream") {
        type = "streams";
      } else if (formattedType === "Social Stream") {
        type = "social_streams";
      }

      return type;
    },
  },
};
</script>

<style lang="scss" scoped>
.scoped :deep(.streem-modal .container) {
  max-width: inherit;
}

table .wrap-table-content {
  white-space: initial;
}

.list {
  max-height: 60px;
}
</style>
